import { isRunControlDisabled } from '../../titleAnalysis';
import type { TitleAnalysisPhase } from '../../components/titleAnalysis/TitleAnalysisSection';

/**
 * Pure helpers for the Title analysis run control (label, disabled state and
 * the reason shown to the user). No React, so the rules can be property-tested.
 *
 * Requirements: 11.2, 11.3, 11.4.
 */

export const RUN_LABEL_NO_REPORT = 'Run title analysis';
export const RUN_LABEL_WITH_REPORT = 'Re-run';
export const RUN_LABEL_RETRY = 'Retry';
export const RUN_LABEL_RUNNING = 'Running…';

export const NO_USE_CASE_REASON = 'Select a use case to run the analysis.';
export const RUN_ACTIVE_REASON = 'An analysis is running. You can run again when it finishes.';
export const LOADING_REASON = 'Loading the latest analysis…';

/** A run has been requested or is in progress (use case locked, polling on). */
export function isRunInProgress(phase: TitleAnalysisPhase): boolean {
  return phase === 'starting' || phase === 'running';
}

/**
 * The Run control is busy while a run is starting or running, and also while
 * the stored result is still loading (we do not yet know whether a run is
 * active, so starting another would be unsafe).
 */
export function isRunBusy(phase: TitleAnalysisPhase): boolean {
  return phase === 'loading' || isRunInProgress(phase);
}

/** Label per phase: Running… > Retry (failed/stale) > Re-run (report) > Run title analysis. */
export function runControlLabel(phase: TitleAnalysisPhase, hasReport: boolean): string {
  if (isRunInProgress(phase)) return RUN_LABEL_RUNNING;
  if (phase === 'failed' || phase === 'stale') return RUN_LABEL_RETRY;
  return hasReport ? RUN_LABEL_WITH_REPORT : RUN_LABEL_NO_REPORT;
}

/** Disabled iff no use case is selected or a run is active/loading. */
export function isRunDisabled(selectedUseCase: string | null, phase: TitleAnalysisPhase): boolean {
  return isRunControlDisabled(selectedUseCase, isRunBusy(phase));
}

/** Why the Run control is disabled, or null when it is enabled. */
export function runDisabledReason(
  selectedUseCase: string | null,
  phase: TitleAnalysisPhase
): string | null {
  if (!selectedUseCase) return NO_USE_CASE_REASON;
  if (isRunInProgress(phase)) return RUN_ACTIVE_REASON;
  if (phase === 'loading') return LOADING_REASON;
  return null;
}
